import { PropsWithChildren } from 'react';
import { Image, Pressable, StyleSheet, View } from 'react-native';
import Animated, {
  measure,
  runOnUI,
  useAnimatedRef,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';

import { backIcon } from '@/assets/images/icons';
import { useColor } from '@/hooks/useColor';

import Text from './Text';

const Chevron = ({ progress }: any) => {
  const iconStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${progress.value * -180}deg` }]
  }));
  return (
    <Animated.View style={iconStyle}>
      <Image source={backIcon} style={styles.chevron} />
    </Animated.View>
  );
};

type Props = PropsWithChildren<{
  title?: string;
  description?: string;
  disabled?: boolean;
}>;

const ListAccordion = ({ title, description, children, disabled }: Props) => {
  const colors = useColor();
  const listRef = useAnimatedRef();
  const heightValue = useSharedValue(0);
  const open = useSharedValue(false);
  const isAnimating = useSharedValue(false);
  const progress = useDerivedValue(() => (open.value ? withTiming(0) : withTiming(1)));

  const heightAnimationStyle = useAnimatedStyle(() => ({
    height: heightValue.value
  }));

  const onToggle = () => {
    runOnUI(() => {
      'worklet';
      if (isAnimating.value) return;
      isAnimating.value = true;
      if (heightValue.value === 0) {
        heightValue.value = withTiming(measure(listRef)!.height, {}, () => {
          isAnimating.value = false;
        });
      } else {
        heightValue.value = withTiming(0, {}, () => {
          isAnimating.value = false;
        });
      }
      open.value = !open.value;
    })();
  };

  return (
    <View style={styles.container}>
      <Pressable onPress={onToggle} disabled={disabled} style={styles.headerContainer}>
        <View style={styles.titleContainer}>
          {title && (
            <Text style={{ ...styles.groupLabel, color: colors.titleText }} fontWeight="700">
              {title}
            </Text>
          )}
          {description && <Text style={{ color: colors.titleText }}>{description}</Text>}
        </View>
        <Chevron progress={progress} />
      </Pressable>
      <Animated.View style={heightAnimationStyle}>
        <Animated.View style={styles.contentContainer} ref={listRef}>
          {children}
        </Animated.View>
      </Animated.View>
    </View>
  );
};

export default ListAccordion;

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 0,
    marginVertical: 0,
    overflow: 'hidden'
  },

  titleContainer: {
    flexDirection: 'column',
    gap: 5
  },

  headerContainer: {
    padding: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },

  contentContainer: {
    position: 'absolute',
    width: '100%',
    top: 0,
    paddingTop: 15
  },

  chevron: {
    width: 24,
    height: 24
  },

  groupLabel: {
    fontSize: 18
  }
});
